// CANONICAL COPY (MNSA-01, the GH #7115 pattern). src/components/bia/biaAuthority.js re-exports this file so the browser and the
// Function App share one definition. Edit here, never there.
'use strict';

const { realEmployees } = require('../sampleRecords.cjs');

/**
 * src/components/bia/biaAuthority.js
 *
 * GH #7191 (decision A; #7123 decision e) — the browser's view of who may
 * complete a BIA. The server enforces the same rule
 * (azure-functions/shared/assets/biaAuthority.js, BIA-B-004); this copy only
 * decides which buttons to show, so the user is offered "Submit for approval"
 * rather than a Complete button the server would refuse.
 *
 * The BIA owner is a USER id. A BIA stored before #7191 may carry the asset
 * owner's EMPLOYEE id instead; it resolves through employee.linkedUserId.
 */

const ADMIN_ROLES = ['admin', 'super-admin'];

function isTenantAdmin(session) {
  return !!session && ADMIN_ROLES.includes(session.role);
}

/** True when `owner` (a user id, or a legacy employee id) is `userId`. */
function biaOwnerIsUser(owner, userId, employees) {
  if (!owner || !userId) return false;
  if (owner === userId) return true;
  // A sample person never carries authority (GH #7870).
  const emp = realEmployees(employees || []).find((e) => e && e.id === owner);
  return !!(emp && emp.linkedUserId && emp.linkedUserId === userId);
}

/** True when the signed-in user may move this BIA into 'completed'. */
function canApproveBia(owner, session, employees) {
  return isTenantAdmin(session) || biaOwnerIsUser(owner, session?.userId, employees);
}

/** True when `userId` owns the asset (via the asset's owning employee). */
function isAssetOwner(userId, asset, employees) {
  return !!(asset?.ownerId && biaOwnerIsUser(asset.ownerId, userId, employees));
}

/**
 * For a BIA not yet stored: a non-admin may complete it only as its owner
 * AND as the owner of the asset it rates — the server refuses a user who
 * names themselves owner of someone else's asset (co-owner review of #7216).
 */
function canApproveNewBia(owner, asset, session, employees) {
  if (isTenantAdmin(session)) return true;
  const userId = session?.userId;
  return biaOwnerIsUser(owner, userId, employees) && isAssetOwner(userId, asset, employees);
}

/**
 * The BIA owner to store when an asset is picked: the user linked to the
 * asset's owning employee, or null when that employee has no login — an
 * employee id is never stored as the BIA owner.
 */
function biaOwnerFromAsset(asset, employees) {
  if (!asset?.ownerId) return null;
  const emp = realEmployees(employees || []).find((e) => e && e.id === asset.ownerId);
  return emp?.linkedUserId || null;
}

/**
 * The manual-vs-BIA disagreement for this BIA's asset, the same rule the
 * server reports as criticalityConflicts (BIA-B-002): the asset's value is
 * manual, this BIA is completed and rates it differently. Null otherwise.
 * `biaValue` is the BIA's stored (server-recomputed) rating.
 */
function biaCriticalityConflict(bia, assets, resolve) {
  if (!bia || bia.status !== 'completed' || !bia.assetId) return null;
  const asset = (assets || []).find((a) => a && a.id === bia.assetId);
  if (!asset || asset.criticalitySource !== 'manual') return null;
  const biaValue = resolve ? resolve(bia.overallCriticality) : bia.overallCriticality;
  if (biaValue === null || biaValue === undefined || biaValue === asset.criticality) return null;
  return { assetId: asset.id, manualValue: asset.criticality ?? null, biaValue, biaId: bia.id };
}

module.exports = {
  isTenantAdmin,
  biaOwnerIsUser,
  canApproveBia,
  isAssetOwner,
  canApproveNewBia,
  biaOwnerFromAsset,
  biaCriticalityConflict,
};
